import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { ReactComponent as Warn } from '@/assets/img/warn.svg';
import AreaCodeSelector from '@/ui/AreaCodeSelector';
import { Style } from '@/components/style';
import ServeType from '@/constants/ServeType';
import Validator from '@/utils/validator';
import EmployeeInput from './employeeInput';
import { setDeliveryPhoneError } from '@/actions/uberDirect/uberDirectAction';
import DeliveryType from '@/constants/DeliveryType';

function PhoneInput({
  selectedAreaCode,
  areaCodes,
  setSelectedAreaCode,
  phoneRef,
  employeeRef,
  required,
  checkCTABtnStatus,
}) {
  const { alertColor, gray3Color, blackColor } = new Style().getUiConfig();
  const intl = useIntl();
  const dispatch = useDispatch<any>();
  const projectName = useSelector<RootStateOrAny, any>(
    (state) => state.modeReducer.projectName
  );

  const customizedServeType = useSelector<RootStateOrAny, any>(
    (state) => state.modeReducer.customizedServeType
  );
  switch (projectName) {
    case 'PHISON':
      if (customizedServeType === 'GUEST') {
        return (
          <div
            className="d-flex justify-content-between"
            style={{ marginTop: '20px', position: 'relative', height: '44px' }}
          >
            <AreaCodeSelector
              areaCode={selectedAreaCode}
              setAreaCode={setSelectedAreaCode}
              areaCodeList={areaCodes}
            />
            <input
              className="phone"
              ref={phoneRef}
              style={{
                border: `1px solid ${gray3Color}`,
                color: `${blackColor}`,
              }}
              type="tel"
              id="phone"
              data-testid="phone"
              placeholder={intl.formatMessage({ id: 'tel.placeholder' })}
            />
          </div>
        );
      }
      return <EmployeeInput employeeRef={employeeRef} />;

    default: {
      const [isVaild, setIsVaild] = useState(true);
      const [vaildatorMessage, setVaildatorMessage] = useState('');
      const mobile = useSelector<RootStateOrAny, any>(
        (state) =>
          state.welcomeReducer.webStoreVoResponse.data.orderVo.table.mobile
      );
      const selectedServeType = useSelector<RootStateOrAny, any>(
        (state) => state.modeReducer.serveType
      );
      const uberDirectPhoneErrorMsg = useSelector<RootStateOrAny, any>(
        (state) => state.uberDirectReducer.phoneError
      );
      const deliveryPlatform = useSelector<RootStateOrAny, any>(
        (state) =>
          state.welcomeReducer.webStoreVoResponse.data.webStoreVo
            .deliveryPlatform
      );

      const [phone, setPhone] = useState<string>(mobile);

      useEffect(() => {
        if (selectedServeType !== ServeType.DELIVERY) return;
        if (deliveryPlatform !== DeliveryType.UBER_DIRECT) return;
        const itiAreaCode = selectedAreaCode;
        const isPhoneValid = Validator.phoneValidator(
          phoneRef.current.value,
          itiAreaCode.iti,
          true
        );

        if (phone) {
          setIsVaild(isPhoneValid);
          setVaildatorMessage(
            intl.formatMessage({ id: 'error.invalidMobile' })
          );
        }

        if (uberDirectPhoneErrorMsg) {
          setIsVaild(false);
          setVaildatorMessage(
            intl.formatMessage({ id: 'error.invalidMobile' })
          );
        }
      }, [phoneRef.current?.value, selectedAreaCode, uberDirectPhoneErrorMsg]);

      return (
        <div>
          <div
            className="d-flex mt-3 w-100 gap-2 justify-content-between"
            style={{ position: 'relative', height: '44px' }}
          >
            <AreaCodeSelector
              areaCode={selectedAreaCode}
              setAreaCode={setSelectedAreaCode}
              areaCodeList={areaCodes}
            />
            <input
              className="phone"
              style={{
                border: `1px solid ${gray3Color}`,
                color: `${blackColor}`,
                paddingLeft: '10px',
              }}
              type="tel"
              id="phone"
              data-testid="phone"
              ref={phoneRef}
              placeholder={intl.formatMessage({
                id: `${required ? 'welcome.tel.required' : 'tel.placeholder'}`,
              })}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (uberDirectPhoneErrorMsg) {
                  dispatch(setDeliveryPhoneError(null));
                }
                checkCTABtnStatus();
              }}
            />
          </div>
          {!isVaild && selectedServeType === ServeType.DELIVERY ? (
            <div
              className="d-flex gap-1 align-items-center"
              style={{ color: alertColor, fontSize: '12px' }}
            >
              <Warn />
              {vaildatorMessage}
            </div>
          ) : (
            ''
          )}
        </div>
      );
    }
  }
}

PhoneInput.defaultProps = {
  required: false,
  employeeRef: null,
  checkCTABtnStatus: () => {},
};

PhoneInput.propTypes = {
  phoneRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  employeeRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  selectedAreaCode: PropTypes.shape({
    display: PropTypes.string,
  }).isRequired,
  setSelectedAreaCode: PropTypes.func.isRequired,
  areaCodes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  required: PropTypes.bool,
  checkCTABtnStatus: PropTypes.func,
};

export default PhoneInput;
